import { Routes, Route, useLocation } from 'react-router-dom'
import { Navigation } from './components/Navigation'
import { Footer } from './components/Footer'
import { PasswordProtection } from './components/PasswordProtection'
import { VersionProvider, useVersion } from './contexts/VersionContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Home } from './pages/Home'
import { Itinerary } from './pages/Itinerary'
import { Accommodation } from './pages/Accommodation'
import { WeddingParty } from './pages/WeddingParty'
import { Photos } from './pages/Photos'
import { Attire } from './pages/Attire'
import { QA } from './pages/QA'
import { Registry } from './pages/Registry'
import { RSVP } from './pages/RSVP'
import { NotFound } from './pages/NotFound'
import { GuestAlbum } from './pages/GuestAlbum'
import { LatestUpdatePromoModal } from './components/LatestUpdatePromoModal'
import { LatestUpdatePromoBanner } from './components/LatestUpdatePromoBanner'
import type { LatestUpdatePromoSurface } from './content/latest-update-promo'

const GUEST_ALBUM_PATH = '/guest-album'

function AppContent() {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const isPublicGuestAlbum = location.pathname === GUEST_ALBUM_PATH
  const { isV2, isDefault } = useVersion()
  const { isAuthenticated, isCheckingAuth, login } = useAuth()

  // Define which routes are available for each version
  const shouldShowItinerary = isDefault || isV2
  const shouldShowWeddingParty = isDefault || isV2
  const shouldShowRSVP = isDefault || isV2

  const latestUpdatePromoSurface: LatestUpdatePromoSurface =
    isCheckingAuth || isPublicGuestAlbum
      ? null
      : !isAuthenticated
        ? 'login'
        : isHomePage
          ? 'home'
          : null

  // Show loading state while checking authentication (skip for public routes)
  if (isCheckingAuth && !isPublicGuestAlbum) {
    return (
      <>
        <LatestUpdatePromoModal surface={latestUpdatePromoSurface} />
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-primary-600 font-medium">Loading...</p>
          </div>
        </div>
      </>
    )
  }

  if (!isAuthenticated) {
    if (isPublicGuestAlbum) {
      return <GuestAlbum />
    }
    return (
      <>
        <LatestUpdatePromoModal surface={latestUpdatePromoSurface} />
        <LatestUpdatePromoBanner surface={latestUpdatePromoSurface} />
        <PasswordProtection onSuccess={login} />
      </>
    )
  }

  return (
    <>
      <LatestUpdatePromoModal surface={latestUpdatePromoSurface} />
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation />
        <LatestUpdatePromoBanner
          surface={latestUpdatePromoSurface}
          offsetForFixedNav
        />
        <main className={`flex-1 ${isHomePage ? '' : 'pt-16'}`}>
          <Routes>
            <Route path="/" element={<Home />} />
            {shouldShowItinerary && <Route path="/itinerary" element={<Itinerary />} />}
            <Route path="/accommodations" element={<Accommodation />} />
            {shouldShowWeddingParty && <Route path="/wedding-party" element={<WeddingParty />} />}
            <Route path="/photos" element={<Photos />} />
            <Route path="/attire" element={<Attire />} />
            <Route path="/qa" element={<QA />} />
            <Route path="/registry" element={<Registry />} />
            {shouldShowRSVP && <Route path="/rsvp" element={<RSVP />} />}
            <Route path={GUEST_ALBUM_PATH} element={<GuestAlbum />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <VersionProvider>
        <AppContent />
      </VersionProvider>
    </AuthProvider>
  )
}

export default App